import { create } from 'zustand';
import type { Eval } from '@/types/evals-types';
import { getEvaluations } from '@/services/evaluation/get-evaluations';
import { evalV2ToEval, getEvalsV2 } from '@/services/evaluation/eval-v2';
import * as Sentry from '@sentry/react';
import { HIDDEN_EVAL_TYPES } from '@/types/eval-v2-types';

interface EvalCatalogStore {
  evals: Eval[];
  descriptions: Record<string, string>;
  isLoading: boolean;
  hasFetched: boolean;
  fetch: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Merged v1 + v2 eval definitions used by the node picker / multi-select.
 *
 * Fetches once on first call and caches the result so that rapidly
 * mounting / unmounting NodeInputRenderer doesn't re-hit the API.
 * Call `refresh()` for the manual refresh button.
 */
export const useEvalCatalogStore = create<EvalCatalogStore>((set, get) => ({
  evals: [],
  descriptions: {},
  isLoading: false,
  hasFetched: false,

  fetch: async () => {
    if (get().hasFetched || get().isLoading) return;
    await get().refresh();
  },

  refresh: async () => {
    if (get().isLoading) return;
    set({ isLoading: true });
    try {
      const [v1Result, v2Result] = await Promise.allSettled([
        getEvaluations(),
        getEvalsV2(),
      ]);
      const v1 = v1Result.status === 'fulfilled' ? v1Result.value : [];
      const v2 = v2Result.status === 'fulfilled' ? v2Result.value : [];

      const byId = new Map<string, Eval>();
      for (const e of v1) if (e.id) byId.set(e.id, e);
      for (const e of v2) if (e.id) byId.set(e.id, evalV2ToEval(e));

      // Drop hidden eval types so they don't appear in the
      // workflow-node attach picker. See HIDDEN_EVAL_TYPES in
      // eval-v2-types.ts for the canonical list and re-enable steps.
      const evals = Array.from(byId.values()).filter(
        (e) => !HIDDEN_EVAL_TYPES.has(e.type ?? ''),
      );
      const descriptions: Record<string, string> = {};
      for (const e of evals) {
        if (e.id && e.description) descriptions[e.id] = e.description;
      }

      set({ evals, descriptions, hasFetched: true });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'eval-catalog-store-refresh' },
      });
    } finally {
      set({ isLoading: false });
    }
  },
}));
